import { useState } from 'react'
import { ImagePlus, Trash2 } from 'lucide-react'

import type { ShopMedia } from '@/lib/api/shop-media-api'
import { MediaPicker } from '@/components/media/media-picker'
import { Button } from '@/components/ui/button'
import { thumbOf } from '@/lib/api/shop-catalog-api'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

/** Ảnh dạng object {url, thumb_url} (nội dung trang chủ, hợp đồng §14.20). */
export interface ShopImageValue {
    url: string
    thumb_url?: string | null
}

interface BaseProps {
    id: string
    label: string
    /** Khung ảnh vuông (mặc định) — ảnh bìa / biểu tượng 1:1. */
    className?: string
    disabled?: boolean
}

/** Mặc định: giá trị là path /uploads/... */
interface PathProps extends BaseProps {
    value: string | null
    onChange: (url: string | null) => void
    asObject?: false
}

/** `asObject`: giá trị là object {url, thumb_url} (nội dung trang chủ). */
interface ObjectProps extends BaseProps {
    value: ShopImageValue | null
    onChange: (img: { url: string; thumb_url: string } | null) => void
    asObject: true
}

type Props = PathProps | ObjectProps

function previewOf(value: string | ShopImageValue): string {
    return typeof value === 'string'
        ? thumbOf(value)
        : value.thumb_url || thumbOf(value.url)
}

/**
 * Ô ảnh của màn Bán lẻ: "Chọn ảnh" mở thư viện ảnh (MediaPicker, tải ảnh mới
 * ngay trong hộp) → giữ path /uploads/... hoặc object {url, thumb_url}.
 */
export function ShopImageField(props: Props) {
    const { id, label, value, className, disabled } = props
    const [open, setOpen] = useState(false)

    const choose = ([item]: ShopMedia[]) => {
        if (!item) return
        if (props.asObject)
            props.onChange({ url: item.url, thumb_url: thumbOf(item.url) })
        else props.onChange(item.url)
    }

    return (
        <div className="space-y-1.5">
            <div
                className={cn(
                    'relative flex aspect-square w-full max-w-48 items-center justify-center overflow-hidden rounded-md border bg-muted',
                    className,
                )}
            >
                {value ? (
                    <img
                        src={previewOf(value)}
                        alt={label}
                        className="size-full object-cover"
                    />
                ) : (
                    <ImagePlus className="size-8 text-muted-foreground" />
                )}
            </div>
            <div className="flex gap-2">
                <Button
                    id={id}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    aria-label={m.media_choose_aria({
                        action: value
                            ? m.shop_image_replace()
                            : m.shop_image_choose(),
                        label,
                    })}
                    onClick={() => setOpen(true)}
                >
                    {value ? m.shop_image_replace() : m.shop_image_choose()}
                </Button>
                {value && (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        disabled={disabled}
                        aria-label={m.shop_image_remove_aria({ label })}
                        onClick={() => props.onChange(null)}
                    >
                        <Trash2 className="size-4" />
                    </Button>
                )}
            </div>
            <MediaPicker
                open={open}
                onOpenChange={setOpen}
                kind="image"
                onSelect={choose}
                title={label}
            />
        </div>
    )
}
